import { MOBILE_EVENT_CODE } from '@mobileTypes'

export interface EventContextParam {
  name: string
  type: string
  description: string
}

export interface EventContext {
  description: string
  availableParams: EventContextParam[]
}

export const MOBILE_EVENT_CONTEXTS: Record<MOBILE_EVENT_CODE, EventContext> = {
  [MOBILE_EVENT_CODE.ON_INIT]: {
    description: 'Triggered when screen or form initializes',
    availableParams: [
      { name: 'initialData', type: 'object', description: 'Initial data passed to screen/form' },
      { name: 'routeParams', type: 'object', description: 'Navigation parameters (screen, item, etc.)' },
      { name: 'item', type: 'object', description: 'Item passed from navigation (if any)' },
    ],
  },
  [MOBILE_EVENT_CODE.ON_UNMOUNT]: {
    description: 'Triggered when screen or form unmounts (cleanup)',
    availableParams: [],
  },
  [MOBILE_EVENT_CODE.ON_LIST_ITEM_PRESS]: {
    description: 'Triggered when user taps on a list item',
    availableParams: [
      { name: 'item', type: 'object', description: 'The tapped item data' },
      { name: 'index', type: 'number', description: 'Item index in the list' },
      { name: 'list', type: 'array', description: 'Full list data' },
    ],
  },
  [MOBILE_EVENT_CODE.ON_BUTTON_PRESS]: {
    description: 'Triggered when user clicks a button',
    availableParams: [
      { name: 'elementInternalId', type: 'string', description: 'Button internal ID' },
      { name: 'formData', type: 'object', description: 'Current form data' },
      { name: 'item', type: 'object', description: 'Current item context (if available)' },
    ],
  },
  [MOBILE_EVENT_CODE.ON_BUTTON_SWIPE]: {
    description: 'Triggered when user swipes on a swipe element',
    availableParams: [
      { name: 'elementInternalId', type: 'string', description: 'Swipe internal ID' },
      { name: 'item', type: 'object', description: 'The swiped item data' },
      { name: 'direction', type: 'string', description: 'Swipe direction (left/right)' },
      { name: 'formData', type: 'object', description: 'Current form data' },
    ],
  },
  [MOBILE_EVENT_CODE.ON_FIELD_VALUE_CHANGE]: {
    description: 'Triggered when field value changes',
    availableParams: [
      { name: 'fieldCode', type: 'string', description: 'Field identifier' },
      { name: 'fieldInternalId', type: 'string', description: 'Field internal ID' },
      { name: 'oldValue', type: 'any', description: 'Previous value' },
      { name: 'newValue', type: 'any', description: 'New value' },
      { name: 'formData', type: 'object', description: 'Current form data' },
    ],
  },
  [MOBILE_EVENT_CODE.ON_VARIABLE_CHANGE]: {
    description: 'Triggered when variable changes',
    availableParams: [
      { name: 'variableName', type: 'string', description: 'Variable name' },
      { name: 'oldValue', type: 'any', description: 'Previous value' },
      { name: 'newValue', type: 'any', description: 'New value' },
    ],
  },
  [MOBILE_EVENT_CODE.ON_INTERVAL]: {
    description: 'Triggered periodically by a timer interval',
    availableParams: [
      { name: 'intervalId', type: 'string', description: 'Timer identifier' },
      { name: 'tick', type: 'number', description: 'Current tick number (starts from 1)' },
      { name: 'timestamp', type: 'number', description: 'Timestamp when tick occurred' },
    ],
  },
  [MOBILE_EVENT_CODE.ON_CHECKBOX_EMBEDDED_LIST_CHANGE]: {
    description: 'Triggered when checkbox state changes in embedded list',
    availableParams: [
      { name: 'item', type: 'object', description: 'The item whose checkbox was changed' },
      { name: 'checked', type: 'boolean', description: 'New checkbox state' },
      { name: 'selectedIds', type: 'array', description: 'Array of all selected item IDs' },
      { name: 'elementId', type: 'string', description: 'ID of the EMBEDDED_LIST element' },
    ],
  },
  [MOBILE_EVENT_CODE.ON_EMBEDDED_LIST_ITEM_CONTEXT_MENU]: {
    description: 'Triggered when context menu button is clicked on list item',
    availableParams: [
      { name: 'item', type: 'object', description: 'The item for which menu was opened' },
      { name: 'itemId', type: 'string', description: 'ID of the item' },
      { name: 'selectedIds', type: 'array', description: 'Current selected item IDs' },
      { name: 'elementId', type: 'string', description: 'ID of the EMBEDDED_LIST element' },
      { name: 'actionInternalId', type: 'string', description: 'internalId of the selected context menu action' },
    ],
  },
  [MOBILE_EVENT_CODE.ON_DRAWER_ITEM_PRESS]: {
    description: 'Triggered when user taps a drawer menu item',
    availableParams: [
      { name: 'item', type: 'object', description: 'The tapped drawer item config' },
      { name: 'config', type: 'object', description: 'Current app config' },
      { name: 'drawerItemInternalId', type: 'string', description: 'Internal ID of the tapped drawer item' },
    ],
  },
  [MOBILE_EVENT_CODE.ON_FAB_PRESS]: {
    description: 'Triggered when user taps the FAB (Floating Action Button) on a list screen',
    availableParams: [
      { name: 'list', type: 'array', description: 'Current list items' },
    ],
  },
  [MOBILE_EVENT_CODE.ON_SHEET_OPENED]: {
    description: 'Triggered when Split screen bottom sheet is opened',
    availableParams: [
      { name: 'snapIndex', type: 'number', description: 'Index of the snap point the sheet opened to' },
      { name: 'backgroundVariables', type: 'object', description: 'Variables from background (list/form)' },
      { name: 'sheetFormVariables', type: 'object', description: 'Variables from sheet form' },
    ],
  },
  [MOBILE_EVENT_CODE.ON_SHEET_CLOSED]: {
    description: 'Triggered when Split screen bottom sheet is closed',
    availableParams: [
      { name: 'backgroundVariables', type: 'object', description: 'Variables from background (list/form)' },
      { name: 'sheetFormVariables', type: 'object', description: 'Variables from sheet form' },
    ],
  },
  [MOBILE_EVENT_CODE.ON_CONDITION]: {
    description: 'Branch flow executed by a CONDITION action based on evaluated value',
    availableParams: [
      { name: 'conditionValue', type: 'any', description: 'The value that triggered this branch' },
      { name: 'conditionBranchId', type: 'string', description: 'Branch identifier' },
    ],
  },
}

